import React, { useState, useEffect, useMemo, useRef } from "react"
import { Modal, Label, Input } from "reactstrap"
import { useTable } from "react-table"
import NewPagination from "components/Common/Newpagination"
import Loader from "pages/Separate/Loader"
import Swal from "sweetalert2"
import axios from "axios"
import configure from "configure"
import {
  fetchNodeData,
  FetchNodePostData,
  FetchNodePutData,
} from "utils/common-fetchData"
import secureLocalStorage from "react-secure-storage"
import moment from "moment"
import { formatDate, formatDateTime } from "utils/formatDate"

const PASS_SCORE = 70
const EXCELLENCE_SCORE = 90
const PHONE_REGEX = /^\d{10}$/

export function scoreColor(s) {
  if (s >= EXCELLENCE_SCORE) return "#16a34a"
  if (s >= PASS_SCORE) return "#d97706"
  return "#dc2626"
}
export function scoreBg(s) {
  if (s >= EXCELLENCE_SCORE) return "#f0fdf4"
  if (s >= PASS_SCORE) return "#fffbeb"
  return "#fff5f5"
}
export function scoreBorder(s) {
  if (s >= EXCELLENCE_SCORE) return "#86efac"
  if (s >= PASS_SCORE) return "#fde68a"
  return "#fecaca"
}
export function scoreLeftBorder(s) {
  if (s >= EXCELLENCE_SCORE) return "#22c55e"
  if (s >= PASS_SCORE) return "#f59e0b"
  return "#ef4444"
}

const VIO_META = {
  tab_switch: {
    icon: "fas fa-exchange-alt",
    color: "#e67e22",
    bg: "#fff8e1",
    border: "#ffc107",
  },
  window_blur: {
    icon: "fas fa-desktop",
    color: "#e74c3c",
    bg: "#fdecea",
    border: "#ef9a9a",
  },
  fullscreen_exit: {
    icon: "fas fa-compress-alt",
    color: "#8e44ad",
    bg: "#f3e5f5",
    border: "#ce93d8",
  },
}

function ViolationsModal({ isOpen, onClose, violations = [], personName }) {
  const leaveCount = violations.filter(
    v => v.kind === "leave" || !v.kind
  ).length

  return (
    <Modal isOpen={isOpen} size="md" centered toggle={onClose}>
      <div className="modal-header px-4">
        <h5 className="modal-title mt-0">
          <i
            className="fas fa-shield-alt me-2"
            style={{ color: "#e74c3c" }}
          ></i>
          Proctoring Activity
          {personName && (
            <span
              className="ms-2 fw-normal"
              style={{ fontSize: 14, color: "#666" }}
            >
              — {personName}
            </span>
          )}
        </h5>
        <button type="button" onClick={onClose} className="btn-modal-close">
          <span aria-hidden="true" className="btn-modal-span">
            &times;
          </span>
        </button>
      </div>
      <div className="modal-body px-4 py-3">
        {violations.length === 0 ? (
          <div className="text-center py-4">
            <i
              className="fas fa-check-circle"
              style={{ fontSize: 36, color: "#27ae60" }}
            ></i>
            <p className="mt-2 mb-0" style={{ color: "#555" }}>
              No violations recorded for this session.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
              <span
                className="badge"
                style={{ background: "#e74c3c", fontSize: 12 }}
              >
                {leaveCount} violation{leaveCount !== 1 ? "s" : ""}
              </span>
            </div>
            <div style={{ position: "relative", paddingLeft: 24 }}>
              <div
                style={{
                  position: "absolute",
                  left: 8,
                  top: 8,
                  bottom: 8,
                  width: 2,
                  background: "#e9ecef",
                  borderRadius: 2,
                }}
              />
              {violations.map((v, i) => {
                const isResume =
                  v.kind === "resume" || v.type === "exam_resumed"
                const meta = isResume
                  ? {
                      icon: "fas fa-play-circle",
                      color: "#27ae60",
                      bg: "#e8f5e9",
                      border: "#a5d6a7",
                    }
                  : VIO_META[v.type] || {
                      icon: "fas fa-exclamation-triangle",
                      color: "#555",
                      bg: "#f5f5f5",
                      border: "#ccc",
                    }

                return (
                  <div
                    key={i}
                    style={{ position: "relative", marginBottom: 10 }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        left: -20,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        background: meta.color,
                        border: "2px solid #fff",
                        boxShadow: `0 0 0 2px ${meta.color}`,
                      }}
                    />
                    <div
                      className="d-flex align-items-center gap-2 rounded px-3 py-2"
                      style={{
                        background: meta.bg,
                        border: `1.5px solid ${meta.border}`,
                      }}
                    >
                      <i
                        className={meta.icon}
                        style={{
                          color: meta.color,
                          fontSize: 14,
                          minWidth: 16,
                        }}
                      ></i>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: meta.color,
                          }}
                        >
                          {v.label}
                          {isResume && v.durationAway != null && (
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 400,
                                color: "#666",
                                marginLeft: 6,
                              }}
                            >
                              (away {v.durationAway}s)
                            </span>
                          )}
                        </div>
                        {!isResume && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "#888",
                              marginTop: 1,
                            }}
                          >
                            {v.type === "tab_switch" &&
                              "Switched to another browser tab"}
                            {v.type === "window_blur" &&
                              "Switched to another application or OS window"}
                            {v.type === "fullscreen_exit" &&
                              "Exited fullscreen mode"}
                          </div>
                        )}
                        {v.question_index != null && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "#666",
                              marginTop: 2,
                            }}
                          >
                            <i
                              className="fas fa-question-circle me-1"
                              style={{ color: "#752a97" }}
                            ></i>
                            On Question {v.question_index}
                          </div>
                        )}
                      </div>
                      {v.time && (
                        <span
                          style={{
                            fontSize: 11,
                            color: "#888",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <i className="fas fa-clock me-1"></i>
                          {v.time}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

function AnswersModal({
  isOpen,
  onClose,
  answers = [],
  personName,
  testName,
  loading,
}) {
  return (
    <Modal isOpen={isOpen} size="xl" centered toggle={onClose}>
      <div className="modal-header pe-4">
        <div>
          <h5 className="modal-title mt-0">
            <i className="fas fa-list-ol me-2 text-purple"></i>
            {testName}
          </h5>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
            {personName}
          </div>
        </div>
        <button type="button" onClick={onClose} className="btn-modal-close">
          <span aria-hidden="true" className="btn-modal-span">
            &times;
          </span>
        </button>
      </div>
      <div
        className="modal-body p-4"
        style={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        {loading ? (
          <div className="text-center text-muted py-4">
            <i className="fas fa-spinner fa-spin fa-2x mb-2 d-block"></i>Loading
            answers…
          </div>
        ) : answers.length === 0 ? (
          <div className="text-center text-muted py-4">
            No answers recorded.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {answers.map((ans, idx) => {
              const qText = ans.question?.question || `Question ${idx + 1}`
              const qType = ans.type ?? ans.question?.type
              const isMcq = qType === 1
              const isCode = qType === 3
              const isCorrect = ans.isCorrect
              const userAns = ans.user_answer ?? "—"
              const correctAns = ans.correct_answer ?? "—"
              const options = (() => {
                try {
                  return ans.question?.options
                    ? JSON.parse(ans.question.options)
                    : null
                } catch {
                  return null
                }
              })()

              // Text/Code questions have no isCorrect — they're scored via reviewer_score/ai_score instead
              const effectiveScore = !isMcq
                ? ans.reviewer_score != null
                  ? ans.reviewer_score
                  : ans.ai_score != null
                  ? ans.ai_score
                  : null
                : null
              const maxScoreVal = ans.max_score || 10
              const scorePct =
                effectiveScore != null
                  ? Math.round((effectiveScore / maxScoreVal) * 100)
                  : null

              const borderCol = isMcq
                ? isCorrect === true
                  ? "#86efac"
                  : isCorrect === false
                  ? "#fecaca"
                  : "#e9d5ff"
                : scorePct == null
                ? "#e9d5ff"
                : scoreBorder(scorePct)
              const leftCol = isMcq
                ? isCorrect === true
                  ? "#22c55e"
                  : isCorrect === false
                  ? "#ef4444"
                  : "#a78bfa"
                : scorePct == null
                ? "#a78bfa"
                : scoreLeftBorder(scorePct)
              const bgCol = isMcq
                ? isCorrect === true
                  ? "#f0fdf4"
                  : isCorrect === false
                  ? "#fff5f5"
                  : "#faf5ff"
                : scorePct == null
                ? "#faf5ff"
                : scoreBg(scorePct)

              return (
                <div
                  key={idx}
                  style={{
                    border: `1px solid ${borderCol}`,
                    borderLeft: `4px solid ${leftCol}`,
                    borderRadius: 8,
                    padding: "12px 14px",
                    background: bgCol,
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: "#1f2937",
                        flex: 1,
                      }}
                    >
                      <span style={{ color: "#752a97", marginRight: 6 }}>
                        Q{idx + 1}.
                      </span>
                      <span
                        className="badge me-1"
                        style={{
                          background: isMcq
                            ? "#0d6efd"
                            : isCode
                            ? "#6f42c1"
                            : "#6c757d",
                          fontSize: 10,
                        }}
                      >
                        {isMcq ? "MCQ" : isCode ? "Code" : "Text"}
                      </span>
                      {qText}
                    </div>
                    <div style={{ whiteSpace: "nowrap" }}>
                      {isMcq ? (
                        isCorrect === true ? (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: "#16a34a",
                              background: "#dcfce7",
                              border: "1px solid #86efac",
                              borderRadius: 20,
                              padding: "2px 8px",
                            }}
                          >
                            <i className="fas fa-check me-1"></i>Correct
                          </span>
                        ) : isCorrect === false ? (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: "#dc2626",
                              background: "#fee2e2",
                              border: "1px solid #fca5a5",
                              borderRadius: 20,
                              padding: "2px 8px",
                            }}
                          >
                            <i className="fas fa-times me-1"></i>Wrong
                          </span>
                        ) : (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: "#6b7280",
                              background: "#f3f4f6",
                              border: "1px solid #d1d5db",
                              borderRadius: 20,
                              padding: "2px 8px",
                            }}
                          >
                            Not Graded
                          </span>
                        )
                      ) : effectiveScore != null ? (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: scoreColor(scorePct),
                            background: scoreBg(scorePct),
                            border: `1px solid ${scoreBorder(scorePct)}`,
                            borderRadius: 20,
                            padding: "2px 8px",
                          }}
                        >
                          {ans.reviewer_override ? (
                            <i
                              className="fas fa-user-edit me-1"
                              style={{ fontSize: 9 }}
                            ></i>
                          ) : ans.ai_graded ? (
                            <i
                              className="fas fa-robot me-1"
                              style={{ fontSize: 9 }}
                            ></i>
                          ) : null}
                          {effectiveScore}/{maxScoreVal} pts
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#c2410c",
                            background: "#fff7ed",
                            border: "1px solid #fed7aa",
                            borderRadius: 20,
                            padding: "2px 8px",
                          }}
                        >
                          <i
                            className="fas fa-clock me-1"
                            style={{ fontSize: 9 }}
                          ></i>
                          Pending Review
                        </span>
                      )}
                    </div>
                  </div>

                  {isMcq && options && (
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {Object.entries(options).map(([k, v]) => {
                        const isUserPick = String(userAns) === String(k)
                        const isCorrectPick = String(correctAns) === String(k)
                        return (
                          <span
                            key={k}
                            style={{
                              fontSize: 11,
                              padding: "3px 10px",
                              borderRadius: 20,
                              border: `1px solid ${
                                isCorrectPick
                                  ? "#86efac"
                                  : isUserPick
                                  ? "#fca5a5"
                                  : "#e5e7eb"
                              }`,
                              background: isCorrectPick
                                ? "#f0fdf4"
                                : isUserPick
                                ? "#fee2e2"
                                : "#f9fafb",
                              color: isCorrectPick
                                ? "#15803d"
                                : isUserPick
                                ? "#b91c1c"
                                : "#6b7280",
                              fontWeight:
                                isUserPick || isCorrectPick ? 600 : 400,
                            }}
                          >
                            {k}. {v}
                            {isUserPick && !isCorrectPick && " ✗"}
                            {isCorrectPick && " ✓"}
                          </span>
                        )
                      })}
                    </div>
                  )}

                  {!isMcq &&
                    (userAns && String(userAns) !== "—" ? (
                      <pre
                        style={{
                          background: isCode ? "#1e1e2e" : "#f8f8f8",
                          color: isCode ? "#cdd6f4" : "#333",
                          borderRadius: 6,
                          padding: "10px 12px",
                          fontSize: 12,
                          fontFamily: isCode ? "monospace" : "inherit",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          margin: "0 0 6px 0",
                        }}
                      >
                        {String(userAns)}
                      </pre>
                    ) : (
                      <span
                        style={{
                          color: "#aaa",
                          fontSize: 12,
                          fontStyle: "italic",
                          display: "block",
                          marginBottom: 6,
                        }}
                      >
                        No answer provided
                      </span>
                    ))}

                  {!isMcq && ans.reviewer_override && ans.ai_score != null && (
                    <div
                      style={{ fontSize: 11, color: "#888", marginBottom: 6 }}
                    >
                      <i className="fas fa-robot me-1"></i>AI scored{" "}
                      {ans.ai_score}/{maxScoreVal}
                      <i className="fas fa-arrow-right mx-1"></i>
                      <i className="fas fa-user-edit me-1"></i>Reviewer overrode
                      to {ans.reviewer_score}/{maxScoreVal}
                    </div>
                  )}

                  {isMcq && (
                    <div style={{ fontSize: 12, color: "#555" }}>
                      <span style={{ fontWeight: 600, color: "#374151" }}>
                        Selected:{" "}
                      </span>
                      <span
                        style={{
                          color: isCorrect === false ? "#dc2626" : "#374151",
                        }}
                      >
                        {String(userAns)}
                      </span>
                    </div>
                  )}

                  {isMcq && isCorrect === false && (
                    <div
                      style={{ fontSize: 12, color: "#16a34a", marginTop: 2 }}
                    >
                      <span style={{ fontWeight: 600 }}>Correct: </span>
                      {String(correctAns)}
                    </div>
                  )}

                  {!isMcq && ans.ai_explanation && (
                    <div
                      className="d-flex align-items-start gap-2 px-2 py-2 rounded mt-1"
                      style={{
                        background: "#f5f0ff",
                        border: "1px solid #d8b4fe",
                        fontSize: 11,
                      }}
                    >
                      <i
                        className="fas fa-robot mt-1"
                        style={{ color: "#752a97", fontSize: 12, minWidth: 13 }}
                      ></i>
                      <span style={{ color: "#444", lineHeight: 1.5 }}>
                        {ans.ai_explanation}
                      </span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </Modal>
  )
}

const POPULATION_LABEL = {
  staff: "staff",
  internal_trainee: "internal trainees",
  external_trainee: "external trainees",
}

const EMPTY_STATS = { assigned: 0, submitted: 0, notSubmitted: 0, avg: 0 }

// A staff member can have one assessment record from the India backend and
// another from the UK backend for the same test. Keep just one: whichever
// has a score (submitted), and if neither/both are submitted, prefer India.
function dedupeStaffEntries(rows) {
  const byKey = new Map()
  for (const row of rows) {
    const key = `${(row.email || "").toLowerCase()}::${row.test_name || ""}`
    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, row)
      continue
    }
    const existingScored = existing.status === "submitted"
    const rowScored = row.status === "submitted"
    if (rowScored && !existingScored) {
      byKey.set(key, row)
    } else if (!rowScored && existingScored) {
      // keep existing — it already has a score
    } else if (existing._region !== "IN" && row._region === "IN") {
      // tie (both submitted or both not) — India wins
      byKey.set(key, row)
    }
  }
  return Array.from(byKey.values())
}

// Self-contained Actual/Mock test-status table for one population (staff, internal_trainee,
// or external_trainee). Used both as the content of Overall Assessment View's main tabs and
// embedded directly inside the External Candidates widget's "External Trainee" tab.
const AssessmentPopulationTable = ({ population }) => {
  const [isLoading, setLoading] = useState(false)
  const [subTab, setSubTab] = useState(0) // 0 = Actual Test, 1 = Mock Test
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"))
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [stats, setStats] = useState(EMPTY_STATS)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [filterStatus, setFilterStatus] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [vioModal, setVioModal] = useState(false)
  const [answersModal, setAnswersModal] = useState(false)
  const [answersData, setAnswersData] = useState([])
  const [answersLoading, setAnswersLoading] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)

  // Promote / Convert trainee actions
  const [selectedTrainee, setSelectedTrainee] = useState(null)
  const [convertModal, setConvertModal] = useState(false)
  const [convertForm, setConvertForm] = useState({
    gender: "",
    phone_number: "",
    department: "",
    designation: "",
    roleid: "",
  })
  const [savingTraineeAction, setSavingTraineeAction] = useState(false)
  const [departmentOptions, setDepartmentOptions] = useState([])
  const [roleOptions, setRoleOptions] = useState([])
  const [convertOptionsLoading, setConvertOptionsLoading] = useState(false)

  const loadData = async () => {
    const token = secureLocalStorage.getItem("auth_token")
    const userId = localStorage.getItem("auth_id")
    if (!userId) return
    const params = new URLSearchParams({
      date,
      test_type: String(subTab),
      population,
      page: String(page),
      pageSize: String(pageSize),
    })
    if (debouncedSearch) params.set("search", debouncedSearch)
    if (filterStatus) params.set("status", filterStatus)
    const [indiaResult, ukResult] = await Promise.allSettled([
      fetchNodeData(
        setLoading,
        `preassesment/report/internalStaffStatus/${userId}?${params.toString()}`,
        token
      ),
      axios.get(
        `${
          configure.nodeUkApi
        }/preassesment/report/internalStaffStatus/${userId}?${params.toString()}`
      ),
    ])

    const india =
      indiaResult.status === "fulfilled" && indiaResult.value?.success
        ? indiaResult.value
        : null
    const uk =
      ukResult.status === "fulfilled" && ukResult.value?.data?.success
        ? ukResult.value.data
        : null

    if (ukResult.status === "rejected") {
      console.log("UK Response Error", ukResult.reason)
      Swal.fire({
        icon: "error",
        title: "Error Fetching UK Assessment Responses",
        text:
          ukResult.reason?.response?.data?.message ||
          ukResult.reason?.message,
      })
    }

    if (india || uk) {
      const indiaStats = india?.stats || EMPTY_STATS
      const ukStats = uk?.stats || EMPTY_STATS
      const submitted = (indiaStats.submitted || 0) + (ukStats.submitted || 0)

      const merged = [
        ...(india?.data || []).map(r => ({ ...r, _region: "IN" })),
        ...(uk?.data || []).map(r => ({ ...r, _region: "UK" })),
      ]
      const finalRows =
        population === "staff" ? dedupeStaffEntries(merged) : merged
      finalRows.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      )
      setData(finalRows)
      setTotal((india?.total || 0) + (uk?.total || 0))
      setStats({
        assigned: (indiaStats.assigned || 0) + (ukStats.assigned || 0),
        submitted,
        notSubmitted:
          (indiaStats.notSubmitted || 0) + (ukStats.notSubmitted || 0),
        avg: submitted
          ? Math.round(
              ((indiaStats.avg || 0) * (indiaStats.submitted || 0) +
                (ukStats.avg || 0) * (ukStats.submitted || 0)) /
                submitted
            )
          : 0,
      })
    }
  }

  // Debounce search so we don't hit the server on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350)
    return () => clearTimeout(t)
  }, [search])

  // Any filter/tab change should snap back to page 1. Combined into a single effect
  // (instead of a separate "reset page" + "fetch" effect) so a filter change never
  // fires a fetch with the stale pre-reset page number — that race let an in-flight
  // request for the old page silently overwrite the correctly-filtered results.
  const prevFiltersRef = useRef({
    population,
    subTab,
    date,
    debouncedSearch,
    filterStatus,
    pageSize,
  })
  useEffect(() => {
    const prev = prevFiltersRef.current
    const filtersChanged =
      prev.population !== population ||
      prev.subTab !== subTab ||
      prev.date !== date ||
      prev.debouncedSearch !== debouncedSearch ||
      prev.filterStatus !== filterStatus ||
      prev.pageSize !== pageSize
    prevFiltersRef.current = {
      population,
      subTab,
      date,
      debouncedSearch,
      filterStatus,
      pageSize,
    }

    if (filtersChanged && page !== 1) {
      setPage(1) // triggers this effect again via the `page` dep, with filtersChanged now false
      return
    }
    loadData()
  }, [population, subTab, date, debouncedSearch, filterStatus, pageSize, page])

  const handlePromote = async row => {
    const confirm = await Swal.fire({
      icon: "question",
      title: "Promote to Internal Trainee?",
      text: `${row.name} will move from External to Internal Trainee.`,
      showCancelButton: true,
      confirmButtonText: "Yes, promote",
      confirmButtonColor: "#752a97",
    })
    if (!confirm.isConfirmed) return

    setSavingTraineeAction(true)
    const userId = localStorage.getItem("auth_id")
    const res = await FetchNodePutData(
      setSavingTraineeAction,
      "trainee/promote",
      row.user_id,
      { user_id: userId }
    )
    if (res?.data?.success) {
      Swal.fire({
        icon: "success",
        title: "Promoted to Internal Trainee",
        timer: 1300,
        showConfirmButton: false,
      })
      loadData()
    } else {
      Swal.fire({
        icon: "error",
        title: "Promotion failed",
        text: res?.data?.message,
        confirmButtonColor: "#752a97",
      })
    }
  }

  const loadConvertOptions = async () => {
    setConvertOptionsLoading(true)
    try {
      const [deptRes, roleRes] = await Promise.all([
        axios.get(`${configure.apiUrl}/departments`, {
          headers: { Accept: "application/json" },
        }),
        axios.get(`${configure.apiUrl}/staffData`, {
          headers: { Accept: "application/json" },
        }),
      ])
      setDepartmentOptions(deptRes.data?.data?.department || [])
      setRoleOptions(roleRes.data?.data?.roles || [])
    } catch (e) {
      console.warn("convert options fetch error", e)
    }
    setConvertOptionsLoading(false)
  }

  const openConvertModal = row => {
    setSelectedTrainee({ id: row.user_id, name: row.name, email: row.email })
    setConvertForm({
      gender: "",
      phone_number: "",
      department: "",
      designation: "",
      roleid: "",
    })
    setConvertModal(true)
    loadConvertOptions()
  }

  const handleConvertSubmit = async e => {
    e.preventDefault()
    if (!convertForm.gender.trim() || !convertForm.phone_number.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Gender and phone number are required",
        confirmButtonColor: "#752a97",
      })
      return
    }
    if (!PHONE_REGEX.test(convertForm.phone_number.trim())) {
      Swal.fire({
        icon: "warning",
        title: "Phone number must be exactly 10 digits",
        confirmButtonColor: "#752a97",
      })
      return
    }
    setSavingTraineeAction(true)
    const token = secureLocalStorage.getItem("auth_token")
    const userId = localStorage.getItem("auth_id")
    const res = await FetchNodePostData(
      setSavingTraineeAction,
      `trainee/convertToStaff/${selectedTrainee.id}`,
      token,
      {
        user_id: userId,
        gender: convertForm.gender.trim(),
        phone_number: convertForm.phone_number.trim(),
        department: convertForm.department || "",
        designation: convertForm.designation || "",
        roleid: convertForm.roleid || "",
      }
    )
    if (res?.data?.success) {
      Swal.fire({
        icon: "success",
        title: "Converted to Staff successfully",
        timer: 1500,
        showConfirmButton: false,
      })
      setConvertModal(false)
      loadData()
    } else {
      Swal.fire({
        icon: "error",
        title: "Conversion failed",
        text: res?.data?.message,
        confirmButtonColor: "#752a97",
      })
    }
  }

  const openViolations = row => {
    setSelectedRow(row)
    setVioModal(true)
  }

  const openAnswers = async row => {
    setSelectedRow(row)
    setAnswersModal(true)
    setAnswersData([])
    if (!row.submission_id) return
    const token = secureLocalStorage.getItem("auth_token")
    const res = await fetchNodeData(
      setAnswersLoading,
      `preassesment/data/getResponse/detail/${row.submission_id}`,
      token
    )
    if (res?.success) setAnswersData(res.data?.answers || [])
  }

  const hasFilters = search || filterStatus

  const columns = useMemo(
    () => [
      {
        Header: "Employee",
        accessor: "name",
        Cell: ({ row }) => (
          <div className="d-flex align-items-start gap-2">
            <span
              title={
                row.original._region === "UK"
                  ? "United Kingdom record"
                  : "India record"
              }
              style={{
                marginTop: 5,
                width: 8,
                height: 8,
                minWidth: 8,
                borderRadius: "50%",
                background:
                  row.original._region === "UK" ? "#2563eb" : "#ea580c",
              }}
            ></span>
            <div>
              <div style={{ fontWeight: 600, color: "#1f2937" }}>
                {row.original.name || "—"}
              </div>
              <div style={{ fontSize: 11, color: "#6b7280" }}>
                {row.original.email}
              </div>
            </div>
          </div>
        ),
      },
      {
        Header: "Test",
        accessor: "test_name",
        className: "fw-medium",
      },
      {
        Header: "Status",
        accessor: "status",
        className: "text-center",
        Cell: ({ value }) =>
          value === "submitted" ? (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                background: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: 20,
                padding: "3px 10px",
                fontSize: 11,
                fontWeight: 600,
                color: "#16a34a",
              }}
            >
              <i className="fas fa-check"></i> Submitted
            </span>
          ) : (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                background: "#fff5f5",
                border: "1px solid #fecaca",
                borderRadius: 20,
                padding: "3px 10px",
                fontSize: 11,
                fontWeight: 600,
                color: "#dc2626",
              }}
            >
              <i className="fas fa-hourglass-half"></i> Not Submitted
            </span>
          ),
      },
      {
        Header: "Score",
        accessor: "score",
        className: "text-center",
        Cell: ({ row }) => {
          if (row.original.status !== "submitted")
            return <span style={{ color: "#d1d5db" }}>—</span>
          const s = row.original.score ?? 0
          return (
            <div
              style={{ fontWeight: 800, fontSize: 14, color: scoreColor(s) }}
            >
              {s}%
            </div>
          )
        },
      },
      {
        Header: "Result",
        id: "result",
        className: "text-center",
        Cell: ({ row }) => {
          if (row.original.status !== "submitted")
            return <span style={{ color: "#d1d5db" }}>—</span>
          const s = row.original.score ?? 0
          return (
            <span
              style={{
                display: "inline-block",
                padding: "3px 10px",
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 600,
                background: scoreBg(s),
                border: `1px solid ${scoreBorder(s)}`,
                color: scoreColor(s),
              }}
            >
              {s >= EXCELLENCE_SCORE
                ? "Excellent"
                : s >= PASS_SCORE
                ? "Pass"
                : "Fail"}
            </span>
          )
        },
      },
      {
        Header: "Violations",
        accessor: "violation_count",
        className: "text-center",
        Cell: ({ row }) => {
          if (row.original.status !== "submitted")
            return <span style={{ color: "#d1d5db" }}>—</span>
          if (!row.original.violation_count)
            return (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  background: "#f0fdf4",
                  border: "1px solid #86efac",
                  borderRadius: 20,
                  padding: "3px 10px",
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#16a34a",
                }}
              >
                <i className="fas fa-shield-alt"></i> Clean
              </span>
            )
          return (
            <button
              className="btn btn-sm p-0"
              style={{ background: "transparent", border: "none" }}
              onClick={() => openViolations(row.original)}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  background: "#fff5f5",
                  border: "1px solid #fecaca",
                  borderRadius: 20,
                  padding: "3px 10px",
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#dc2626",
                }}
              >
                <i className="fas fa-exclamation-triangle"></i>{" "}
                {row.original.violation_count}
              </span>
            </button>
          )
        },
      },
      {
        Header: "Submitted On",
        accessor: "submitted_at",
        Cell: ({ row }) =>
          row.original.status === "submitted"
            ? formatDateTime(row.original.submitted_at) || "—"
            : "—",
      },
      {
        Header: "Answers",
        id: "view_answers",
        className: "text-center",
        Cell: ({ row }) => {
          if (row.original.status !== "submitted")
            return <span style={{ color: "#d1d5db" }}>—</span>
          return (
            <button
              className="btn btn-sm"
              style={{
                border: "1px solid #752a97",
                color: "#752a97",
                background: "transparent",
              }}
              onClick={() => openAnswers(row.original)}
              title="View Answers"
            >
              <i className="fas fa-eye"></i>
            </button>
          )
        },
      },
      ...(population !== "staff"
        ? [
            {
              Header: "Action",
              id: "trainee_action",
              className: "text-center",
              Cell: ({ row }) =>
                population === "external_trainee" ? (
                  <button
                    className="btn btn-sm"
                    style={{
                      border: "1px solid #752a97",
                      color: "#752a97",
                      background: "transparent",
                    }}
                    onClick={() => handlePromote(row.original)}
                    disabled={savingTraineeAction}
                  >
                    <i className="fas fa-arrow-up me-1"></i>Promote
                  </button>
                ) : (
                  <button
                    className="btn btn-sm"
                    style={{
                      border: "1px solid #16a34a",
                      color: "#16a34a",
                      background: "transparent",
                    }}
                    onClick={() => openConvertModal(row.original)}
                    disabled={savingTraineeAction}
                  >
                    <i className="fas fa-user-check me-1"></i>Convert to Staff
                  </button>
                ),
            },
          ]
        : []),
    ],
    [population, savingTraineeAction]
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data })

  // Pagination is server-driven — adapt our page/pageSize/total state to NewPagination's expected shape
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const pageIndex = page - 1

  return (
    <div>
      {isLoading && <Loader />}
      {/* Sub-tabs + Stats + Filters — all in one row */}
      <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-3">
        <div className="d-flex gap-2">
          {[
            { key: 0, label: "Actual Test" },
            { key: 1, label: "Mock Test" },
          ].map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setSubTab(tab.key)}
              style={{
                padding: "5px 16px",
                borderRadius: 20,
                border: subTab === tab.key ? "none" : "1px solid #d8b4fe",
                background:
                  subTab === tab.key
                    ? "linear-gradient(135deg, #752a97, #a855c9)"
                    : "transparent",
                color: subTab === tab.key ? "#fff" : "#752a97",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {stats.assigned > 0 && (
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {[
              {
                label: "Assigned",
                value: stats.assigned,
                color: "#752a97",
                bg: "#f5eeff",
                icon: "fas fa-users",
              },
              {
                label: "Submitted",
                value: stats.submitted,
                color: "#16a34a",
                bg: "#edfbf3",
                icon: "fas fa-check-circle",
              },
              {
                label: "Not Submitted",
                value: stats.notSubmitted,
                color: "#dc2626",
                bg: "#fff5f5",
                icon: "fas fa-times-circle",
              },
              {
                label: "Avg Score",
                value: `${stats.avg}%`,
                color: "#d97706",
                bg: "#fffbeb",
                icon: "fas fa-chart-line",
              },
            ].map(({ label, value, color, bg, icon }) => (
              <div
                key={label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: "#fff",
                  border: `1.5px solid ${color}25`,
                  borderRadius: 10,
                  padding: "6px 12px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 7,
                    background: bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i className={icon} style={{ color, fontSize: 11 }}></i>
                </div>
                <div style={{ lineHeight: 1.2 }}>
                  <div style={{ fontSize: 10, color: "#888", fontWeight: 500 }}>
                    {label}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color }}>
                    {value}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <input
            type="date"
            className="form-control"
            style={{
              width: 150,
              fontSize: 13,
              height: 36,
              opacity: subTab === 0 ? 1 : 0.5,
            }}
            value={date}
            onChange={e => setDate(e.target.value)}
            disabled={subTab !== 0}
            title={
              subTab === 0
                ? undefined
                : "Mock tests aren't date-scoped — this filter only applies to Actual Test"
            }
          />
          <div
            className="position-relative"
            style={{ flex: "1 1 160px", maxWidth: 200 }}
          >
            <i
              className="fas fa-search position-absolute"
              style={{
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#aaa",
                fontSize: 12,
              }}
            ></i>
            <input
              className="form-control"
              style={{
                paddingLeft: 30,
                fontSize: 13,
                borderRadius: 8,
                height: 36,
              }}
              placeholder="Search name or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select
            className="form-select"
            style={{ width: 150, fontSize: 13, height: 36 }}
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="submitted">Submitted</option>
            <option value="not_submitted">Not Submitted</option>
          </select>
          <select
            className="form-select"
            style={{ width: 100, fontSize: 13, height: 36 }}
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
          >
            {[10, 20, 30, 50].map(size => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </select>
          {hasFilters && (
            <button
              className="btn btn-sm rounded-4 btn-red"
              onClick={() => {
                setSearch("")
                setFilterStatus("")
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
      </div>

      <div
        className="d-flex align-items-center gap-3 mb-2"
        style={{ fontSize: 11, color: "#6b7280" }}
      >
        <div className="d-flex align-items-center gap-1">
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#ea580c",
            }}
          ></span>
          India
        </div>
        <div className="d-flex align-items-center gap-1">
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#2563eb",
            }}
          ></span>
          United Kingdom
        </div>
      </div>

      {/* Table */}
      {data.length === 0 && !isLoading ? (
        <div className="text-center text-muted py-4">
          <i className="fas fa-clipboard-list fa-2x mb-2 d-block"></i>
          {hasFilters
            ? `No ${POPULATION_LABEL[population]} match the current filters.`
            : `No ${POPULATION_LABEL[population]} assigned for ${formatDate(
                date
              )}.`}
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table
              className="table table-bordered mb-0"
              style={{ fontSize: 13 }}
              {...getTableProps()}
            >
              <thead className="table-light">
                {headerGroups.map((headerGroup, i) => (
                  <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column, index) => (
                      <th
                        key={index}
                        className={column.className}
                        {...column.getHeaderProps()}
                      >
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows.map((row, i) => {
                  prepareRow(row)
                  const submitted = row.original.status === "submitted"
                  const s = row.original.score ?? 0
                  return (
                    <tr
                      key={i}
                      {...row.getRowProps()}
                      style={
                        submitted
                          ? {
                              background: scoreBg(s),
                              borderLeft: `3px solid ${scoreLeftBorder(s)}`,
                            }
                          : {
                              background: "#fafafa",
                              borderLeft: "3px solid #d1d5db",
                            }
                      }
                    >
                      {row.cells.map((cell, index) => (
                        <td
                          key={index}
                          className={cell.column.className}
                          style={{ verticalAlign: "middle" }}
                          {...cell.getCellProps()}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <NewPagination
            gotoPage={p => setPage(p + 1)}
            canPreviousPage={page > 1}
            previousPage={() => setPage(p => Math.max(1, p - 1))}
            canNextPage={page < totalPages}
            pageIndex={pageIndex}
            pageOptions={Array.from({ length: totalPages })}
            pageSize={pageSize}
            page={rows}
            data={{ length: total }}
            nextPage={() => setPage(p => Math.min(totalPages, p + 1))}
          />
        </>
      )}

      <ViolationsModal
        isOpen={vioModal}
        onClose={() => setVioModal(false)}
        violations={selectedRow?.violations || []}
        personName={selectedRow?.name}
      />

      <AnswersModal
        isOpen={answersModal}
        onClose={() => setAnswersModal(false)}
        answers={answersData}
        loading={answersLoading}
        personName={selectedRow?.name}
        testName={selectedRow?.test_name}
      />

      {/* Convert Trainee to Staff Modal */}
      <Modal
        isOpen={convertModal}
        size="lg"
        centered
        toggle={() => setConvertModal(false)}
      >
        <div className="modal-header px-4 py-3">
          <h5 className="modal-title mt-0" style={{ fontSize: 15 }}>
            <i className="fas fa-user-check me-2 text-purple"></i>
            Convert to Staff
          </h5>
          <button
            type="button"
            onClick={() => setConvertModal(false)}
            className="btn-modal-close"
          >
            <span aria-hidden="true" className="btn-modal-span">
              &times;
            </span>
          </button>
        </div>
        <div className="modal-body px-4 py-3">
          {selectedTrainee && (
            <div className="mb-3" style={{ fontSize: 12, color: "#6b7280" }}>
              Converting <strong>{selectedTrainee.name}</strong> (
              {selectedTrainee.email}) into a real Staff record.
            </div>
          )}
          <form onSubmit={handleConvertSubmit}>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>
                  Gender <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  value={convertForm.gender}
                  onChange={e =>
                    setConvertForm(prev => ({
                      ...prev,
                      gender: e.target.value,
                    }))
                  }
                  required
                >
                  <option value="">Select…</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </Input>
              </div>
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>
                  Phone Number <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  maxLength={10}
                  value={convertForm.phone_number}
                  onChange={e =>
                    setConvertForm(prev => ({
                      ...prev,
                      phone_number: e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 10),
                    }))
                  }
                  required
                />
              </div>
            </div>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>
                  Department
                </Label>
                <Input
                  type="select"
                  value={convertForm.department}
                  onChange={e =>
                    setConvertForm(prev => ({
                      ...prev,
                      department: e.target.value,
                    }))
                  }
                  disabled={convertOptionsLoading}
                >
                  <option value="">
                    {convertOptionsLoading ? "Loading…" : "Select…"}
                  </option>
                  {departmentOptions.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.department_name}
                    </option>
                  ))}
                </Input>
              </div>
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>
                  Role
                </Label>
                <Input
                  type="select"
                  value={convertForm.roleid}
                  onChange={e =>
                    setConvertForm(prev => ({
                      ...prev,
                      roleid: e.target.value,
                    }))
                  }
                  disabled={convertOptionsLoading}
                >
                  <option value="">
                    {convertOptionsLoading ? "Loading…" : "Select…"}
                  </option>
                  {roleOptions.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.role_name}
                    </option>
                  ))}
                </Input>
              </div>
            </div>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>
                  Designation
                </Label>
                <Input
                  type="text"
                  value={convertForm.designation}
                  onChange={e =>
                    setConvertForm(prev => ({
                      ...prev,
                      designation: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div
              className="mt-4 pt-3"
              style={{ borderTop: "1px solid #e9ecef" }}
            >
              <button
                type="submit"
                className="border-radius btn btn-purple btn-sm px-4"
                disabled={savingTraineeAction}
              >
                <i className="fas fa-user-check me-2"></i>
                {savingTraineeAction ? "Converting…" : "Convert to Staff"}
              </button>
              <button
                type="button"
                className="border-radius btn btn-light btn-sm px-4 ms-2"
                onClick={() => setConvertModal(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  )
}

export default AssessmentPopulationTable
